import NotFound from "@/components/common/pages/not-found";
import { StatusBadge } from "@/components/status-badge";
import { OrderItemsDialog } from "@/components/tables/_components/order-items-dialog";
import { buildOrderItemDrafts } from "@/components/tables/_components/order-items-form";
import { OrderStatusDialog } from "@/components/tables/_components/order-status-dialog";
import { parseSkuMatchResults } from "@/components/tables/sku-matches-dropdown";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFeatureFlags } from "@/hooks/use-feature-flags";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useMS365Status } from "@/hooks/use-ms365-status";
import { useZohoStatus } from "@/hooks/use-zoho-status";
import { getSupplierFacingOrderLabel } from "@/lib/order-display";
import { useAuth } from "@/providers/auth";
import { MatchersService } from "@/services/matchers-service";
import { OrderService } from "@/services/order-services";
import type { Order } from "@/services/types/orders";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  notFound,
  useCanGoBack,
  useRouter,
} from "@tanstack/react-router";
import {
  ArrowUpToLine,
  ChevronLeft,
  Loader2,
  Pencil,
  SquarePen,
  Truck,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import OrderTabContent from "./-components/order-tabs-content";

export const Route = createFileRoute("/_main/orders/$orderId")({
  component: RouteComponent,
  loader: async ({ params }) => {
    const order = await OrderService.getOrderById(params.orderId);

    if (!order) {
      throw notFound();
    }

    return order;
  },
  notFoundComponent: () => <NotFound />,
});

function RouteComponent() {
  const [activeTab, setActiveTab] = useState("order_details");
  const ordr = Route.useLoaderData();
  const [currentOrder, setCurrentOrder] = useState(ordr);
  const [orderItemsDraft, setOrderItemsDraft] = useState(() =>
    buildOrderItemDrafts(ordr),
  );
  const isMobile = useMediaQuery("(max-width: 640px)");

  const router = useRouter();
  const queryClient = useQueryClient();
  const canGoBack = useCanGoBack();
  const { status: zohoStatus } = useZohoStatus();
  const { status: ms365Status } = useMS365Status();
  const { allowPastOrderDeliveryDates } = useFeatureFlags();
  const { user } = useAuth();
  const isWarehouseUser = user?.supplierRole === "warehouse_user";
  const isPrivilegedUser = ["supplier_admin", "department_admin"].includes(
    user?.supplierRole ?? "",
  );
  const canProcessStatus = isWarehouseUser
    ? currentOrder.status === "Approved" || currentOrder.status === "Dispatched"
    : isPrivilegedUser
      ? ["Pending", "Approved", "Dispatched"].includes(currentOrder.status)
      : currentOrder.status === "Pending";

  const hasERPIntegration =
    Boolean(zohoStatus?.connected) || Boolean(ms365Status?.connected);

  if (!ordr) {
    router.navigate({ to: "/orders" });
  }

  useEffect(() => {
    setCurrentOrder(ordr);
    setOrderItemsDraft(buildOrderItemDrafts(ordr));
  }, [ordr]);

  useEffect(() => {
    if (
      isWarehouseUser &&
      !["Approved", "Dispatched"].includes(currentOrder.status)
    ) {
      void router.navigate({ to: "/orders" });
    }
  }, [currentOrder.status, isWarehouseUser, router]);

  const applyPersistedOrder = useCallback((persistedOrder: Order) => {
    setCurrentOrder(persistedOrder);
    setOrderItemsDraft(buildOrderItemDrafts(persistedOrder));
  }, []);

  const updateOrder = useCallback(
    async (orderId: number, ord: Order): Promise<void> => {
      const persistedOrder = await OrderService.updateOrderPut(orderId, ord);
      applyPersistedOrder(persistedOrder);
    },
    [applyPersistedOrder],
  );

  const orderWithItemDraft = useMemo(
    () => ({
      ...currentOrder,
      order_details: orderItemsDraft,
    }),
    [currentOrder, orderItemsDraft],
  );

  const hasDirtyOrderItems = useMemo(
    () =>
      JSON.stringify(orderItemsDraft) !==
      JSON.stringify(buildOrderItemDrafts(currentOrder)),
    [currentOrder, orderItemsDraft],
  );

  const persistOrderItemsDraft = useCallback(async () => {
    if (!hasDirtyOrderItems) {
      return;
    }

    await updateOrder(currentOrder.id, orderWithItemDraft);
  }, [currentOrder.id, hasDirtyOrderItems, orderWithItemDraft, updateOrder]);

  const handleDeliveryDateChange = (nextDate: string | null) => {
    setCurrentOrder((previousOrder) => ({
      ...previousOrder,
      expected_delivery_date: nextDate,
    }));
  };

  const handleDeliveryAddressChange = (nextAddress: string) => {
    setCurrentOrder((previousOrder) => ({
      ...previousOrder,
      restaurant_address: nextAddress,
    }));
  };

  const { data: skuMatchData } = useQuery({
    queryKey: ["sku-match-results", currentOrder.id],
    queryFn: () => MatchersService.getSkuMatchResults(currentOrder.id),
    enabled: !!currentOrder.id,
    staleTime: 60_000,
  });

  const skuMatchesByIndex = useMemo(
    () => parseSkuMatchResults(skuMatchData),
    [skuMatchData],
  );

  const erpOrderIdLabel = currentOrder.erp_order_id ?? "Not sent";
  const orderMetaTypography = {
    fontFamily: "DM Sans, sans-serif",
    fontSize: "24px",
    lineHeight: "140%",
    letterSpacing: "0px",
  };

  const { mutate: pushOrderToERP, isPending: isERPCreating } = useMutation({
    mutationKey: ["push-order-to-erp", currentOrder.id],
    mutationFn: () =>
      OrderService.pushOrderToERPAndWaitForERPId(currentOrder.id),
    onSuccess: (erpOrderId) => {
      void queryClient.invalidateQueries({ queryKey: ["orders"] });

      if (erpOrderId) {
        setCurrentOrder((previousOrder) => ({
          ...previousOrder,
          erp_order_id: erpOrderId,
        }));
        toast.success("Order synced to ERP");
        return;
      }

      toast.success("Order queued for ERP sync");
      void router.invalidate();
    },
    onError: () => {
      toast.error("Failed to queue order for ERP sync");
    },
  });

  const statusVariant =
    currentOrder.status === "Pending"
      ? "pending"
      : ["Approved", "Dispatched", "Delivered"].includes(currentOrder.status)
        ? "success"
        : "error";
  const mobileProcessOrderAction =
    isMobile && canProcessStatus ? (
      <OrderStatusDialog
        order={orderWithItemDraft}
        onBeforeSave={hasDirtyOrderItems ? persistOrderItemsDraft : undefined}
        onSave={(_, updatedOrder) => applyPersistedOrder(updatedOrder)}
        trigger={
          <Button
            variant="default"
            disabled={!canProcessStatus}
            className="h-[52px] w-full rounded-lg bg-black text-base text-white hover:bg-black/90"
          >
            Process Order
            <Truck className="h-[22px] w-[22px]" />
          </Button>
        }
      />
    ) : null;

  return (
    <div>
      {!isMobile ? (
        <Button
          variant="link"
          className="mt-8 ml-4"
          onClick={() => router.history.back()}
          disabled={!canGoBack}
        >
          <ChevronLeft />
          Go back
        </Button>
      ) : null}

      <div className={isMobile ? "pb-8" : "max-w-7xl p-4"}>
        {isMobile ? (
          <div className="space-y-4 px-4 pt-2 pb-4">
            <Button
              variant="link"
              className="h-auto px-0 font-normal text-[#495057] text-sm"
              onClick={() => router.history.back()}
              disabled={!canGoBack}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Back to Orders
            </Button>

            <div className="space-y-3">
              <div className="flex items-center gap-2.5">
                <h2 className="font-medium text-[#212529] text-[22px]">
                  Order #{getSupplierFacingOrderLabel(currentOrder)}
                </h2>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge
                    status={currentOrder.status}
                    variant={statusVariant}
                    className={
                      currentOrder.status === "Pending"
                        ? "gap-1.5 bg-[#fffaeb] px-2.5 py-[3px] text-[#b54708] text-sm [&>div]:size-1.5 [&>div]:bg-[#f79009]"
                        : "gap-1.5 px-2.5 py-[3px] text-sm [&>div]:size-1.5"
                    }
                  />
                  {activeTab !== "delivery" && currentOrder.erp_order_id ? (
                    <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-600 text-xs">
                      ERP {currentOrder.erp_order_id}
                    </span>
                  ) : null}
                </div>
              </div>

              {activeTab !== "delivery" ? (
                <div className="flex items-center gap-2">
                  {!isWarehouseUser ? (
                    <OrderItemsDialog
                      order={orderWithItemDraft}
                      onSave={updateOrder}
                      skuMatchesByIndex={skuMatchesByIndex}
                      trigger={
                        <Button
                          variant="outline"
                          size="icon"
                          disabled={currentOrder.status !== "Pending"}
                          className="h-12 w-12 rounded-xl border-slate-200 bg-white text-slate-700"
                        >
                          <SquarePen className="h-5 w-5" />
                          <span className="sr-only">Edit order details</span>
                        </Button>
                      }
                    />
                  ) : null}

                  {currentOrder.status === "Approved" ? (
                    isERPCreating ? (
                      <Button
                        variant="outline"
                        size="icon"
                        disabled
                        className="h-12 w-12 rounded-xl border-slate-200"
                      >
                        <Loader2 className="h-5 w-5 animate-spin" />
                      </Button>
                    ) : currentOrder.erp_order_id == null ? (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => pushOrderToERP()}
                        disabled={!hasERPIntegration}
                        className="h-12 w-12 rounded-xl border-slate-200 bg-white text-slate-700"
                      >
                        <ArrowUpToLine className="h-5 w-5" />
                        <span className="sr-only">Send to ERP</span>
                      </Button>
                    ) : null
                  ) : null}

                  {canProcessStatus ? (
                    <OrderStatusDialog
                      order={orderWithItemDraft}
                      onBeforeSave={
                        hasDirtyOrderItems ? persistOrderItemsDraft : undefined
                      }
                      onSave={(_, updatedOrder) =>
                        applyPersistedOrder(updatedOrder)
                      }
                      trigger={
                        <Button
                          variant="default"
                          size="icon"
                          disabled={!canProcessStatus}
                          className="h-12 w-12 rounded-xl bg-black text-white hover:bg-black/90"
                        >
                          <Truck className="h-5 w-5" />
                          <span className="sr-only">Process order</span>
                        </Button>
                      }
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="mb-8 space-y-1">
            <div className="flex items-center gap-2">
              <p className="text-foreground" style={orderMetaTypography}>
                <span className="font-semibold">Order ID:</span>{" "}
                <span className="font-normal">
                  {getSupplierFacingOrderLabel(currentOrder)}
                </span>
                <span className="px-2 font-normal">|</span>
                <span className="font-semibold">ERP ID:</span>{" "}
                <span className="font-normal">{erpOrderIdLabel}</span>
              </p>
              <StatusBadge
                status={currentOrder.status}
                variant={statusVariant}
              />
            </div>
          </div>
        )}

        <Tabs
          defaultValue="order_details"
          value={activeTab}
          onValueChange={setActiveTab}
          className="custom-tabs w-full"
        >
          <div className="flex flex-col md:flex-row md:justify-between">
            <TabsList
              className={
                isMobile
                  ? "mb-0 flex w-full justify-start gap-[22px] border-0 bg-transparent px-4"
                  : "mb-4 flex h-[38px] w-full justify-start overflow-x-auto border-gray-200 border-b bg-transparent p-0 md:mb-0 md:w-[531px]"
              }
            >
              <TabsTrigger
                value="order_details"
                className={
                  isMobile
                    ? "!rounded-none border-b-2 border-b-transparent px-0 py-2 text-[#6c757d] text-sm data-[state=active]:border-b-[#102d03] data-[state=active]:font-medium data-[state=active]:text-[#212529] data-[state=active]:shadow-none"
                    : "!rounded-none relative h-[38px] w-[175px] flex-none border-b-2 border-b-transparent px-6 py-2 text-gray-600 data-[state=active]:border-b-2 data-[state=active]:border-b-black data-[state=active]:font-medium data-[state=active]:text-black data-[state=active]:shadow-none"
                }
              >
                Order Details
              </TabsTrigger>
              <TabsTrigger
                value="customer_info"
                className={
                  isMobile
                    ? "!rounded-none border-b-2 border-b-transparent px-0 py-2 text-[#6c757d] text-sm data-[state=active]:border-b-[#102d03] data-[state=active]:font-medium data-[state=active]:text-[#212529] data-[state=active]:shadow-none"
                    : "!rounded-none relative h-[38px] w-[175px] flex-none border-b-2 border-b-transparent px-6 py-2 text-gray-600 data-[state=active]:border-b-2 data-[state=active]:border-b-black data-[state=active]:font-medium data-[state=active]:text-black data-[state=active]:shadow-none"
                }
              >
                Customer's Info
              </TabsTrigger>
              <TabsTrigger
                value="delivery"
                className={
                  isMobile
                    ? "!rounded-none border-b-2 border-b-transparent px-0 py-2 text-[#6c757d] text-sm data-[state=active]:border-b-[#102d03] data-[state=active]:font-medium data-[state=active]:text-[#212529] data-[state=active]:shadow-none"
                    : "!rounded-none relative h-[38px] w-[181px] flex-none border-b-2 border-b-transparent px-6 py-2 text-gray-600 data-[state=active]:border-b-2 data-[state=active]:border-b-black data-[state=active]:font-medium data-[state=active]:text-black data-[state=active]:shadow-none"
                }
              >
                Delivery
              </TabsTrigger>
            </TabsList>

            {!isMobile && canProcessStatus ? (
              <div className="mt-4 flex gap-2 md:mt-0">
                {!isWarehouseUser ? (
                  <OrderItemsDialog
                    order={orderWithItemDraft}
                    onSave={updateOrder}
                    skuMatchesByIndex={skuMatchesByIndex}
                    trigger={
                      <Button
                        variant="outline"
                        disabled={currentOrder.status !== "Pending"}
                        className="flex items-center gap-2"
                      >
                        <Pencil className="h-4 w-4" />
                        <span>Edit</span>
                      </Button>
                    }
                  />
                ) : null}
                <OrderStatusDialog
                  order={orderWithItemDraft}
                  onBeforeSave={
                    hasDirtyOrderItems ? persistOrderItemsDraft : undefined
                  }
                  onSave={(_, updatedOrder) =>
                    applyPersistedOrder(updatedOrder)
                  }
                  trigger={
                    <Button variant="default" disabled={!canProcessStatus}>
                      Process Order
                      <Truck className="h-4 w-4" />
                    </Button>
                  }
                />
              </div>
            ) : null}

            {!isMobile && currentOrder.status === "Approved" ? (
              <div className="mt-4 flex gap-2 md:mt-0">
                {isERPCreating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : currentOrder.erp_order_id == null ? (
                  <Button
                    onClick={() => pushOrderToERP()}
                    disabled={!hasERPIntegration}
                  >
                    Send to ERP
                  </Button>
                ) : (
                  <div className="rounded-md bg-green-100 px-2 py-0.5 text-center text-green-800 text-xs">
                    <div>ERP Order ID</div>
                    <span className="text-sm">{currentOrder.erp_order_id}</span>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <TabsContent
            value="order_details"
            className={isMobile ? "mt-0" : "mt-6"}
          >
            <OrderTabContent
              activeTab="order_details"
              updateFunction={updateOrder}
              order={currentOrder}
              orderItemsDraft={orderItemsDraft}
              onOrderItemsDraftChange={setOrderItemsDraft}
              onDeliveryDateChange={handleDeliveryDateChange}
              onDeliveryAddressChange={handleDeliveryAddressChange}
              processOrderAction={mobileProcessOrderAction}
            />
          </TabsContent>

          <TabsContent
            value="customer_info"
            className={isMobile ? "mt-0" : "mt-6"}
          >
            <OrderTabContent
              activeTab="customer_info"
              updateFunction={updateOrder}
              order={currentOrder}
              onDeliveryDateChange={handleDeliveryDateChange}
              onDeliveryAddressChange={handleDeliveryAddressChange}
              processOrderAction={mobileProcessOrderAction}
            />
          </TabsContent>

          <TabsContent value="delivery" className={isMobile ? "mt-0" : "mt-6"}>
            <OrderTabContent
              activeTab="delivery"
              allowPastDeliveryDates={allowPastOrderDeliveryDates}
              updateFunction={updateOrder}
              order={currentOrder}
              onDeliveryDateChange={handleDeliveryDateChange}
              onDeliveryAddressChange={handleDeliveryAddressChange}
              processOrderAction={mobileProcessOrderAction}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
